/**
 * Shared attachment-upload state for the support widget: add via file picker,
 * clipboard paste, or drag-drop; each file uploads immediately through
 * `useFileUpload` (fileService.uploadFile) and resolves to a `SupportAttachment`
 * ref. Used by both `SupportComposer` (thread replies) and `ComposeView`
 * (new request) so the upload/paste/drop behavior stays identical.
 *
 * Cleanup: a file finishes uploading to blob storage the moment it's picked —
 * before the user has decided to send anything. If they remove the chip or
 * abandon compose without sending, that blob would otherwise sit in Azure
 * forever, referenced by no message and therefore invisible to the audited
 * participant-scoped attachment endpoint. `removeAttachment`/`reset` fire a
 * best-effort `DELETE /files/:id` for any entry that finished uploading.
 */

import { useCallback, useRef, useState } from 'react';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { deleteFileAsset } from '../../../services/api/fileService';
import type { SupportAttachment } from '@upskill/shared';

const MAX_ATTACHMENTS = 5;

export interface PendingAttachment {
  clientId: string;
  file: File;
  uploading: boolean;
  error: boolean;
  attachment?: SupportAttachment;
}

/** Best-effort delete — cleanup must never block the UI on failure. */
function deleteUploadedAssetBestEffort(fileAssetId: string): void {
  deleteFileAsset(fileAssetId).catch(() => {
    // Swallow: worst case is an orphaned blob for admin cleanup, not a UI break.
  });
}

export function useSupportAttachments() {
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const { upload } = useFileUpload();

  // React 18 StrictMode double-invokes state updater functions, and `pending`
  // read from the updater's `current` lags behind fast successive calls
  // (paste, then drop, in the same tick) — a ref is the single source of
  // truth for "how many are already queued" so the cap can't be bypassed and
  // so upload/side-effect calls never live inside a (must-be-pure) updater.
  const pendingRef = useRef<PendingAttachment[]>([]);
  const setPendingState = useCallback((updater: (prev: PendingAttachment[]) => PendingAttachment[]) => {
    setPending((prev) => {
      const next = updater(prev);
      pendingRef.current = next;
      return next;
    });
  }, []);

  const addFiles = useCallback(
    (files: File[]) => {
      const room = MAX_ATTACHMENTS - pendingRef.current.length;
      if (room <= 0) return;
      const toAdd = files.slice(0, room);

      const placeholders: PendingAttachment[] = toAdd.map((file) => ({
        clientId: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
        uploading: true,
        error: false,
      }));
      setPendingState((prev) => [...prev, ...placeholders]);

      // Uploads (side effects) kick off after the pure state update above,
      // never inside a setState updater.
      placeholders.forEach(({ clientId, file }) => {
        upload(file)
          .then(({ id }) => {
            setPendingState((prev) =>
              prev.map((p) =>
                p.clientId === clientId
                  ? {
                      ...p,
                      uploading: false,
                      attachment: {
                        fileAssetId: id,
                        filename: file.name,
                        contentType: file.type || 'application/octet-stream',
                        size: file.size,
                      },
                    }
                  : p,
              ),
            );
          })
          .catch(() => {
            setPendingState((prev) =>
              prev.map((p) => (p.clientId === clientId ? { ...p, uploading: false, error: true } : p)),
            );
          });
      });
    },
    [setPendingState, upload],
  );

  const removeAttachment = useCallback(
    (clientId: string) => {
      const removed = pendingRef.current.find((p) => p.clientId === clientId);
      setPendingState((prev) => prev.filter((p) => p.clientId !== clientId));
      if (removed?.attachment) deleteUploadedAssetBestEffort(removed.attachment.fileAssetId);
    },
    [setPendingState],
  );

  const reset = useCallback(() => {
    for (const p of pendingRef.current) {
      if (p.attachment) deleteUploadedAssetBestEffort(p.attachment.fileAssetId);
    }
    setPendingState(() => []);
  }, [setPendingState]);

  const handlePasteImage = useCallback(
    (e: React.ClipboardEvent): boolean => {
      for (const item of e.clipboardData.items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) addFiles([file]);
          return true;
        }
      }
      return false;
    },
    [addFiles],
  );

  const handleDropFiles = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length) addFiles(files);
    },
    [addFiles],
  );

  const readyAttachments = pending.filter((p) => p.attachment).map((p) => p.attachment as SupportAttachment);
  const hasUploading = pending.some((p) => p.uploading);
  const hasFailed = pending.some((p) => p.error);

  return {
    pending,
    addFiles,
    removeAttachment,
    reset,
    handlePasteImage,
    handleDropFiles,
    readyAttachments,
    hasUploading,
    hasFailed,
  };
}
